import React, { useEffect, useMemo, useState } from "react";
import "./Finance.css";
import { getApiBaseUrl } from "../utils/apiConfig";

const API = `${getApiBaseUrl()}/admin/finance`;
const getAdminHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('jwt_token') || localStorage.getItem('token')}`
});

function CountUp({ to = 0, duration = 900, prefix = "" }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = null, raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{prefix}{val.toLocaleString()}</>;
}

const money = (n) => "LKR " + Number(n || 0).toLocaleString();
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

function StatusBadge({ statusId, status, onClick }) {
  const map = { 1: "badge green as-button", 2: "badge amber as-button" };
  return <button className={map[statusId] || "badge gray"} onClick={onClick}>{status}</button>;
}

export default function Finance() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [summary, setSummary] = useState({ totalPayments: 0, paidPayments: 0, pendingPayments: 0 });
  const [bookings, setBookings] = useState([]);
  const [tourBookings, setTourBookings] = useState([]);
  const [visaPayments, setVisaPayments] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [filteredTourBookings, setFilteredTourBookings] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [view, setView] = useState("all");

  const cards = useMemo(() => [
    { title: "Total Payments", value: summary.totalPayments, sub: "All booking, tour & visa payments", icon: "🧾", currency: false, prefix: "+" },
    { title: "Paid Payments", value: summary.paidPayments, sub: "Total paid payments", icon: "✅", currency: true },
    { title: "Pending Payments", value: summary.pendingPayments, sub: "Total pending payments", icon: "⏳", currency: true },
  ], [summary]);

  async function loadData() {
    try {
      setLoading(true);
      setErr("");
      const [summaryRes, bookingsRes, tourBookingsRes, visaPaymentsRes] = await Promise.all([
        fetch(`${API}/summary`, { headers: getAdminHeaders() }),
        fetch(`${API}/bookings`, { headers: getAdminHeaders() }),
        fetch(`${API}/tour-bookings`, { headers: getAdminHeaders() }),
        fetch(`${API}/visa-payments`, { headers: getAdminHeaders() })
      ]);

      const summaryData = await summaryRes.json();
      const bookingsData = await bookingsRes.json();
      const tourBookingsData = await tourBookingsRes.json();
      const visaPaymentsData = await visaPaymentsRes.json();

      setSummary(summaryData);
      setBookings(bookingsData);
      setTourBookings(tourBookingsData);
      setVisaPayments(visaPaymentsData);

      setFilteredBookings(bookingsData);
      setFilteredTourBookings(tourBookingsData);

    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;

    setFilteredBookings(bookings.filter(r => {
      const date = new Date(r.bookingDate);
      if (from && date < from) return false;
      if (to && date > to) return false;
      return true;
    }));

    setFilteredTourBookings(tourBookings.filter(r => {
      const date = new Date(r.bookingDate);
      if (from && date < from) return false;
      if (to && date > to) return false;
      return true;
    }));

  }, [dateFrom, dateTo, bookings, tourBookings]);

  // async function toggleStatus(type, bookingId, currentStatusId) {
  //   const newStatusId = currentStatusId === 1 ? 2 : 1;
  //   try {
  //     const res = await fetch(`${API}/${type}/${bookingId}/status`, {
  //       method: "PATCH",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ statusId: newStatusId })
  //     });
  //     const data = await res.json();
  //     if (!res.ok) throw new Error(data?.message || "Failed");

  //     if (type === "bookings") {
  //       setBookings(prev => prev.map(r => r.bookingId === bookingId ? data.row : r));
  //       setFilteredBookings(prev => prev.map(r => r.bookingId === bookingId ? data.row : r));
  //     } else {
  //       setTourBookings(prev => prev.map(r => r.bookingId === bookingId ? data.row : r));
  //       setFilteredTourBookings(prev => prev.map(r => r.bookingId === bookingId ? data.row : r));
  //     }
  //     setSummary(data.summary);
  //   } catch (e) { alert(e.message); }
  // }

  /* ---------- LOADING VIEW ---------- */
  if (loading) {
    return (
      <div className="wd-page">
        <div className="wd-head">
          <div>
            <h2 className="wd-title">Finance Management</h2>
            <p className="muted">Loading…</p>
          </div>
        </div>

        {/* Skeleton Cards */}
        <section className="wd-cards">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card stat" style={{ opacity: 0.6 }}>
              <div className="stat-top">
                <div className="stat-title">Loading…</div>
                <div className="lock" aria-hidden>…</div>
              </div>
              <div className="stat-value">—</div>
              <div className="stat-sub">—</div>
            </div>
          ))}
        </section>

        {/* Skeleton Table */}
        <section className="card table-card" style={{ padding: 24, color: "gray" }}>
          Loading Booking, Tour & Visa Payments…
        </section>
      </div>
    );
  }

  if (err) return <div className="wd-page" style={{ color: "crimson" }}>{err}</div>;

  return (
    <div className="wd-page">
      <div className="wd-head">
        <div>
          <h2 className="wd-title">Finance Management</h2>
          <p className="muted">Manage bookings, guide bookings & visa payments</p>
        </div>
      </div>

      {/* Cards */}
      <section className="wd-cards">
        {cards.map((s, i) => (
          <div key={i} className="card stat">
            <div className="stat-top">
              <div className="stat-title">{s.title}</div>
              <div className="lock">{s.icon}</div>
            </div>
            <div className="stat-value">{s.currency ? <>LKR <CountUp to={s.value} />.00</> : <CountUp to={s.value} prefix={s.prefix || ""} />}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </section>

      {/* Toolbar: Filters left, Segmented view right */}
      <div className="wd-toolbar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>

        {/* Left container: date filters only for all/hotel/guide, empty for visa */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {(view === "all" || view === "hotel" || view === "guide") && (
            <>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="input" style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", color: dateTo ? "inherit" : "var(--muted)" }} />
              <span className="muted">to</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="input" style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", color: dateTo ? "inherit" : "var(--muted)" }} />
            </>
          )}
        </div>

        {/* Right container: segmented buttons */}
        <div className="segmented">
          <button className={`seg ${view === "all" ? "active" : ""}`} onClick={() => setView("all")}>All</button>
          <button className={`seg ${view === "hotel" ? "active" : ""}`} onClick={() => setView("hotel")}>Hotel Bookings</button>
          <button className={`seg ${view === "guide" ? "active" : ""}`} onClick={() => setView("guide")}>Guide Bookings</button>
          <button className={`seg ${view === "visa" ? "active" : ""}`} onClick={() => setView("visa")}>Visa Applications</button>
        </div>
      </div>

      {/* Hotel Bookings Table */}
      {(view === "all" || view === "hotel") && (
        <section className="card table-card">
          <h3 className="table-title">Hotel Booking Payments</h3>
          <br />
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 34 }}><input type="checkbox" aria-label="select all" /></th>
                <th>Booking ID</th>
                <th>Hotel Name</th>
                <th>Booking Date</th>
                <th>Check-in Date</th>
                <th>Check-out Date</th>
                <th>Payment</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map(r => (
                <tr key={r.bookingId}>
                  <td><input type="checkbox" aria-label={`select ${r.regId}`} /></td>
                  <td>{r.bookingCode}</td>
                  <td>{r.hotelName}</td>
                  <td className="muted">{fmtDate(r.bookingDate)}</td>
                  <td className="muted">{fmtDate(r.checkInDate)}</td>
                  <td className="muted">{fmtDate(r.checkOutDate)}</td>
                  <td className="amount">{money(r.price)}</td>
                  <td><StatusBadge statusId={r.paymentStatusId} status={r.paymentStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Guide Bookings Table */}
      {(view === "all" || view === "guide") && (
        <section className="card table-card">
          <h3 className="table-title">Guide Booking Payments</h3>
          <br />
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 34 }}><input type="checkbox" aria-label="select all" /></th>
                <th>Booking ID</th>
                <th>Guide Name</th>
                <th>Tour Title</th>
                <th>Booking Date</th>
                <th>Date of booked</th>
                <th>Payment</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTourBookings.map(r => (
                <tr key={r.bookingId}>
                  <td><input type="checkbox" aria-label={`select ${r.regId}`} /></td>
                  <td>{r.tourBookingCode}</td>
                  <td>{r.guideName}</td>
                  <td className="muted">{r.tourTitle}</td>
                  <td className="muted">{fmtDate(r.bookingDate)}</td>
                  <td className="muted">{fmtDate(r.createdAt)}</td>
                  <td className="amount">{money(r.price)}</td>
                  <td><StatusBadge statusId={r.paymentStatusId} status={r.paymentStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Visa Application Payments Table */}
      {(view === "all" || view === "visa") && (
        <section className="card table-card">
          <h3 className="table-title">Visa Application Payments</h3>
          <br />
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 34 }}><input type="checkbox" aria-label="select all" /></th>
                <th>Application ID</th>
                <th>Applicant Name</th>
                <th>Passport Number</th>
                <th>Issue Date</th>
                <th>Expiry Date</th>
                <th>Submitted Date</th>
                <th>Payment</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {visaPayments.map(r => (
                <tr key={r.paymentId}>
                  <td><input type="checkbox" aria-label={`select ${r.regId}`} /></td>
                  <td>{r.visaApplicationCode}</td>
                  <td>{r.applicantName}</td>
                  <td className="muted">{r.passportNumber}</td>
                  <td className="muted">{fmtDate(r.passportIssueDate)}</td>
                  <td className="muted">{fmtDate(r.passportExpiryDate)}</td>
                  <td className="muted">{fmtDate(r.applicationDate)}</td>
                  <td className="amount">{money(r.paymentAmount)}</td>
                  <td>
                    <button
                      className={r.paymentStatus.toLowerCase() === "done" ? "badge green as-button" : "badge amber as-button"}
                    >
                      {r.paymentStatus}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

    </div>
  );
}
